import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { format } from 'date-fns';

interface DailyNote {
  id: string;
  user_id: string;
  organization_id: string;
  day_of_week: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export const useDailyNotes = (date: Date) => {
  const { user, currentOrganization } = useAuth();
  const queryClient = useQueryClient();
  const dayOfWeek = format(date, 'EEEE'); // e.g., "Monday"

  const { data: note, isLoading } = useQuery({
    queryKey: ['daily-note', user?.id, currentOrganization?.id, dayOfWeek],
    queryFn: async () => {
      if (!user || !currentOrganization) return null;

      const { data, error } = await supabase
        .from('daily_notes')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_id', currentOrganization.id)
        .eq('day_of_week', dayOfWeek)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data as DailyNote | null;
    },
    enabled: !!user && !!currentOrganization,
  });

  const saveMutation = useMutation({
    mutationFn: async (content: string) => {
      if (!user || !currentOrganization) throw new Error('User not authenticated');

      const noteData = {
        user_id: user.id,
        organization_id: currentOrganization.id,
        day_of_week: dayOfWeek,
        content,
        created_by: user.id,
        updated_by: user.id,
      };

      if (note?.id) {
        const { data, error } = await supabase
          .from('daily_notes')
          .update({ content, updated_by: user.id })
          .eq('id', note.id)
          .select()
          .single();

        if (error) throw error;
        return data;
      } else {
        const { data, error } = await supabase
          .from('daily_notes')
          .insert(noteData)
          .select()
          .single();

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['daily-note', user?.id, currentOrganization?.id, dayOfWeek],
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!note?.id) throw new Error('No note to delete');

      const { error } = await supabase
        .from('daily_notes')
        .delete()
        .eq('id', note.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['daily-note', user?.id, currentOrganization?.id, dayOfWeek],
      });
    },
  });

  return {
    note,
    isLoading,
    saveNote: saveMutation.mutate,
    deleteNote: deleteMutation.mutate,
    isSaving: saveMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};